<template>
    <v-app id="corra">
        <div class="corra-bg" v-if="routeGroup === 'auth'"></div>
        <v-alert type="error" v-model="showError" dismissible tile class="global-error mb-0">{{ appError }}</v-alert>

        <v-app-bar app clipped-left color="primary" dark height="48" v-if="isLoggedIn">
            <v-toolbar-title>
                <a @click="$router.push('/')"><img :src="logoImage" height="24" alt="Corra"></a>
            </v-toolbar-title>
            <router-view name="menu"></router-view>
        </v-app-bar>

        <v-main :class="{'primary': isPrimaryRoute}">
            <router-view></router-view>
            <v-overlay :value="isLoading">
                <v-progress-circular
                    indeterminate
                    size="64"
                ></v-progress-circular>
            </v-overlay>
        </v-main>

        <v-snackbar v-model="showMessage" :timeout="5000" :color="appMessage.color">
            {{ appMessage.text }}
            <template v-slot:action="{ attrs }">
                <v-btn icon v-bind="attrs" @click="showMessage = false">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </template>
        </v-snackbar>
    </v-app>
</template>

<script>
import logoImage from "@/assets/corra-logo.svg";
import editIcon from "@/assets/edit.svg";
import lineChartIcon from "@/assets/line-chart.svg";
import pieChartIcon from "@/assets/pie-chart.svg";
import slidersIcon from "@/assets/sliders.svg";

export default {
    name: 'App',
    components: {},
    data: () => ({
        drawer: null,
        showError: false,
        showMessage: false,
        windowWidth: 0,
        logoImage,
        editIcon,
        lineChartIcon,
        pieChartIcon,
        slidersIcon
    }),
    watch: {
        appError() {
            this.showError = true;
        },
        appMessage() {
            this.showMessage = true;
        },
    },
    created() {
        window.addEventListener("resize", this.updateWindowSize);
        this.updateWindowSize();
    },
    mounted() {
        let mathjaxScript = document.createElement('script');
        mathjaxScript.setAttribute('src', 'https://cdnjs.cloudflare.com/ajax/libs/mathjax/2.7.2/MathJax.js?config=TeX-AMS_HTML');
        document.head.appendChild(mathjaxScript);
    },
    destroyed() {
        window.removeEventListener("resize", this.updateWindowSize);
    },
    methods: {
        async logout() {
            await this.$store.dispatch('logoutUser');
            return this.$router.push({name: 'login'});
        },
        gotoMenuFromBottom(route) {
            this.drawer = true;
            this.menu = route;
        },
        updateWindowSize() {
            this.windowWidth = window.innerWidth;
        }
    },
    computed: {
        appError() {
            return this.$store.state.appError;
        },
        appMessage() {
            return this.$store.state.appMessage;
        },
        routes() {
            return this.$store.getters.allowedRoutes;
        },
        isLoggedIn() {
            return this.$store.getters.isLoggedIn;
        },
        isLoading() {
            return this.$store.state.loading;
        },
        routeGroup() {
            return this.$route?.meta?.group || null;
        },
        isPrimaryRoute() {
            let primaryGroups = this.$store.state.primaryRouteGroups;
            return primaryGroups.indexOf(this.routeGroup) !== -1;
        },
        isMobile() {
            return this.windowWidth < this.$vuetify.breakpoint.thresholds.sm;
        },
        routeIndex() {
            return this.routes.findIndex(route => route.name === this.$route.name);
        }
    },
}
</script>

<style lang="scss">
:root {
    --color-primary: #007672;
    --color-border: #0000001F;
}

sub {
    margin-right: 0.5em;
}

.v-application .error {
    z-index: 100
}

.v-navigation-drawer--fixed {
    z-index: 1000
}

.MathJax_Display {
    margin: 0 0 !important;
}

.frac {
    margin-top: -14px;
}

.primary {
    h2 {
        color: #FFFFFF;
    }
}

.v-toolbar {
    &__title {
        display: flex;
        align-items: center;
        img {
            margin-top: 6px;
        }
    }
}

.v-sheet {
    &.v-app-bar.v-toolbar:not(.v-sheet--outlined) {
        box-shadow: none!important;
        border-bottom: thin solid var(--color-border)!important;
    }
}

.v-dialog {
    background: white;

    &::-webkit-scrollbar {
        width: 12px;
        height: 6px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background-color: rgba(0, 0, 0, 0.2);
        border: 3px solid white;
        border-radius: 6px;
    }
}

.v-radio {
    &__description {
        padding-left: 34px;
        font-size: 80%;
        color: rgba(0, 0, 0, 0.6);
        margin-top: -10px;
        margin-bottom: 10px;
    }
}

.v-tabs {
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.v-stepper {
    &__wrapper {
        overflow: visible!important;
    }
}

.theme--dark {
    &.v-data-table {
        background-color: var(--color-primary) !important;

        & > .v-data-table__wrapper > table {
            thead  {
                & > tr > th {
                    color: #FFFFFF!important;
                }
                & > tr:last-child > th {
                    border-bottom: thin solid var(--color-border)!important;
                }
            }

            tbody {
                & > tr:not(:last-child) > td:last-child, & > tr:not(:last-child) > th:last-child {
                    border-bottom: thin solid var(--color-border)!important;
                }


                & > tr:not(:last-child) > td:not(.v-data-table__mobile-row), & > tr:not(:last-child) > th:not(.v-data-table__mobile-row) {
                    border-bottom: thin solid var(--color-border)!important;
                }

                & > tr:hover:not(.v-data-table__expanded__content):not(.v-data-table__empty-wrapper) {
                    background: var(--color-border)!important;
                }
            }
        }

        .v-data-footer {
            border-top: thin solid var(--color-border)!important;
        }
    }

    &.v-btn.primary {
        background-color: #FFFFFF!important;
        border-color: #FFFFFF!important;
        color: var(--color-primary)!important;
    }
}

.theme--light {
    &.v-label, p {
        color: #434850!important;
    }
    &.v-toolbar.v-sheet {
        background: transparent!important;
    }
}

.page-title {
    font-size: 2em;
    font-weight: normal;

    &__primary {
        color: var(--color-primary);
    }
}

.allow {
    &-overflow {
        overflow: visible!important;
    }
}

.corra-bg {
    width: 100%;
    height: 100%;
    background: var(--color-primary) url("/assets/corra-logo-bg.svg") no-repeat bottom;
    background-size: contain;
    position: fixed;
    bottom: 0;
}

</style>
